import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { CreditCard, Loader2, Lock, Plus, Trash2, Star, X, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const CARD_STYLE = { style: { base: { fontSize: "16px", color: "#1A242B", fontFamily: "inherit", "::placeholder": { color: "#8FA89B" } }, invalid: { color: "#dc2626" } } };

function AddCardForm({ onClose, onSaved }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [makeDefault, setMakeDefault] = useState(true);

  const save = async () => {
    if (!stripe || !elements) return;
    setLoading(true);
    try {
      const { data } = await api.post("/memberships/card/setup-intent");
      const res = await stripe.confirmCardSetup(data.client_secret, {
        payment_method: { card: elements.getElement(CardElement) },
      });
      if (res.error) throw new Error(res.error.message);
      const pmId = res.setupIntent?.payment_method;
      if (res.setupIntent?.status === "succeeded" && pmId) {
        if (makeDefault) {
          try { await api.post(`/memberships/cards/${pmId}/default`); } catch (_) { /* non-fatal */ }
        }
        toast.success("Card saved securely");
        onSaved();
        return;
      }
      throw new Error("Card could not be saved.");
    } catch (e) {
      toast.error(e.response?.data?.detail ? formatApiError(e.response.data.detail) : (e.message || "Could not save card."));
    } finally { setLoading(false); }
  };

  return (
    <div className="rounded-2xl border border-[#E5E0D8] bg-[#F9F7F3] p-5 mt-4" data-testid="add-card-form">
      <div className="rounded-xl border border-[#E5E0D8] bg-white px-4 py-3.5">
        <CardElement options={CARD_STYLE} />
      </div>
      <label className="flex items-center gap-2 mt-3 text-sm text-[#4A5D6B] cursor-pointer">
        <input type="checkbox" checked={makeDefault} onChange={(e) => setMakeDefault(e.target.checked)} data-testid="add-card-default" className="rounded border-[#E5E0D8]" />
        Set as default card
      </label>
      <div className="flex items-center gap-2 mt-4">
        <button onClick={save} disabled={loading || !stripe} data-testid="add-card-save" className="eyw-btn !py-2 text-sm disabled:opacity-60">
          {loading ? <Loader2 size={15} className="animate-spin" /> : <><Lock size={14} /> Save card</>}
        </button>
        <button onClick={onClose} data-testid="add-card-cancel" className="eyw-btn-ghost !py-2 text-sm">Cancel</button>
      </div>
      <p className="text-xs text-[#8FA89B] mt-3 flex items-center gap-1.5"><ShieldCheck size={13} /> Your card is stored securely by Stripe — we never see your full card number.</p>
    </div>
  );
}

export default function SavedCardsPanel({ compact = false }) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [busyPm, setBusyPm] = useState(null);
  const [pk, setPk] = useState(null);

  const { data, isLoading } = useQuery({ queryKey: ["member-cards"], queryFn: () => api.get("/memberships/cards").then((r) => r.data) });
  const cards = data?.cards || [];

  const stripePromise = useMemo(() => (pk ? loadStripe(pk) : null), [pk]);

  const beginAdd = async () => {
    try {
      const { data: d } = await api.post("/memberships/card/setup-intent");
      if (!d.publishable_key) { toast.error("Payments are not configured yet."); return; }
      setPk(d.publishable_key);
      setAdding(true);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const finishAdd = () => {
    setAdding(false);
    qc.invalidateQueries({ queryKey: ["member-cards"] });
    qc.invalidateQueries({ queryKey: ["my-saved-card"] });
  };

  const setDefault = async (pmId) => {
    setBusyPm(pmId);
    try { await api.post(`/memberships/cards/${pmId}/default`); qc.invalidateQueries({ queryKey: ["member-cards"] }); toast.success("Default card updated"); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyPm(null); }
  };

  const remove = async (pmId) => {
    if (!window.confirm("Remove this card?")) return;
    setBusyPm(pmId);
    try {
      await api.delete(`/memberships/cards/${pmId}`);
      qc.invalidateQueries({ queryKey: ["member-cards"] });
      qc.invalidateQueries({ queryKey: ["my-saved-card"] });
      toast.success("Card removed");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyPm(null); }
  };

  return (
    <div className={compact ? "" : "eyw-card p-8 mt-8"} data-testid="saved-cards-panel">
      {!compact && (
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2"><CreditCard size={20} className="text-[#144F75]" /> Payment methods</h2>
        </div>
      )}
      {!compact && <p className="text-sm text-[#4A5D6B] mb-5">Save a card to upgrade your membership in one tap — no front desk needed.</p>}

      {isLoading ? (
        <div className="flex justify-center py-6"><Loader2 className="animate-spin text-[#144F75]" /></div>
      ) : (
        <ul className="space-y-3">
          {cards.map((c) => (
            <li key={c.id} className="flex items-center justify-between border border-[#E5E0D8] rounded-xl px-4 py-3 bg-white" data-testid={`card-row-${c.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <CreditCard size={18} className="text-[#144F75] shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-[#1A242B] capitalize">{c.brand} •••• {c.last4}</div>
                  <div className="text-xs text-[#4A5D6B]">Expires {String(c.exp_month).padStart(2, "0")}/{String(c.exp_year).slice(-2)}</div>
                </div>
                {c.is_default && <span className="text-xs px-2 py-0.5 rounded-full bg-[#144F75]/10 text-[#144F75] font-medium" data-testid={`card-default-badge-${c.id}`}>Default</span>}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {!c.is_default && (
                  <button onClick={() => setDefault(c.id)} disabled={busyPm === c.id} data-testid={`card-make-default-${c.id}`} title="Set as default" className="p-2 rounded-lg text-[#4A5D6B] hover:bg-[#F9F7F3] hover:text-[#144F75] disabled:opacity-50">
                    <Star size={16} />
                  </button>
                )}
                <button onClick={() => remove(c.id)} disabled={busyPm === c.id} data-testid={`card-remove-${c.id}`} title="Remove card" className="p-2 rounded-lg text-[#4A5D6B] hover:bg-red-50 hover:text-red-500 disabled:opacity-50">
                  {busyPm === c.id ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                </button>
              </div>
            </li>
          ))}
          {cards.length === 0 && !adding && (
            <li className="text-sm text-[#8FA89B] py-2" data-testid="cards-empty">No cards saved yet.</li>
          )}
        </ul>
      )}

      {adding && stripePromise ? (
        <Elements stripe={stripePromise}>
          <AddCardForm onClose={() => setAdding(false)} onSaved={finishAdd} />
        </Elements>
      ) : (
        <button onClick={beginAdd} data-testid="add-card-btn" className="eyw-btn-ghost !py-2 text-sm mt-4">
          <Plus size={15} /> Add a card
        </button>
      )}
    </div>
  );
}
